import React, { useState, useEffect, useRef } from 'react';
import { EditorState, convertToRaw, ContentState } from 'draft-js';
import { Editor } from 'react-draft-wysiwyg';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import './RichTextEditor.css';

const RichTextEditor = ({ value, onChange, placeholder = "Type your text here..." }) => {
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [isInitialized, setIsInitialized] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (value && !isInitialized) {
      const contentBlock = htmlToDraft(value);
      if (contentBlock) {
        const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
        const newEditorState = EditorState.createWithContent(contentState);
        setEditorState(newEditorState);
      }
      setIsInitialized(true);
    } else if (!value && !isInitialized) {
      setIsInitialized(true);
    }
  }, [value, isInitialized]);

  const timerRef = useRef(null);

  const onEditorStateChange = (newEditorState) => {
    setEditorState(newEditorState); // Update editor UI instantly
    
    // Debounce the expensive HTML conversion to prevent typing lag
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    
    timerRef.current = setTimeout(() => {
      const contentState = newEditorState.getCurrentContent();
      let htmlValue = draftToHtml(convertToRaw(contentState));
      
      // If the editor has no actual text, treat it as empty to prevent saving `<p></p>`
      if (!contentState.hasText()) {
        htmlValue = '';
      }
      
      if (onChange) {
        onChange(htmlValue);
      }
    }, 400);
  };

  return (
    <div 
      style={{ 
        border: `1px solid ${isFocused ? '#3b82f6' : '#cbd5e1'}`, 
        borderRadius: '0.5rem', 
        background: '#fff', 
        color: '#334155', 
        overflow: 'hidden',
        boxShadow: isFocused ? '0 0 0 3px rgba(59, 130, 246, 0.2)' : '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        transition: 'all 0.2s ease-in-out'
      }}
    >
      <Editor
        editorState={editorState}
        onEditorStateChange={onEditorStateChange}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        placeholder={placeholder}
        wrapperClassName="demo-wrapper"
        editorClassName="axopeople-editor"
        toolbarClassName="axopeople-toolbar"
        editorStyle={{ 
          minHeight: '180px', 
          padding: '1rem', 
          maxHeight: '400px', 
          overflowY: 'auto',
          fontSize: '1rem',
          lineHeight: '1.6'
        }}
        toolbar={{
          options: ['inline', 'blockType', 'list', 'textAlign', 'colorPicker', 'link', 'emoji', 'remove', 'history'],
          inline: { inDropdown: false, options: ['bold', 'italic', 'underline', 'strikethrough'] },
          blockType: { inDropdown: true, options: ['Normal', 'H1', 'H2', 'H3', 'H4', 'Blockquote'] },
          list: { inDropdown: false, options: ['unordered', 'ordered'] },
        }}
      />
    </div>
  );
};

export default RichTextEditor;
